import React, {useContext, useState, useEffect, useCallback, useRef} from "react";
import {flushSync, createPortal} from "react-dom";
import {useNavigate, useParams} from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import axios from "axios";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch, faEllipsisVertical} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {NotificationContext} from "../../contexts/notify.js";
import {AuthContext} from "../../contexts/auth.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import {useSocket} from "../../contexts/socket.js";

// Utils
import {
  buildChecksheetDateScrollUrl,
  formatRecordDateSearchQuery,
  parseChecksheetsDeepLink
} from "../../utils/checksheetsDeepLink.js";
import {getApiBase} from "../../utils/apiBase.js";

// Components
import TableRecord from "../checksheets/TableRecord.js";
import ModalRecordView from "../checksheets/ModalRecordView.js";
import ModalExport from "../checksheets/ModalExport.js";
import ModalArchive from "../checksheets/ModalArchive.js";
import ModalViewAllRecords from "../general/ModalViewAllRecords.js";
import ModalDelete from "../../components/ModalDelete.js";
import ModalFilteredNotifications from "./ModalFilteredNotifications.js";
import Accordion from "../../components/Accordion.js";
import {LISTEN_NOTIFY_FREQUENCY_CHANGE} from "../general/Room.js";

// Style
import {pad, border, radius, transition} from "../../style/components/variables.js";
import {
  Text,
  Loader,
  Button,
  SearchWrapper,
  Search,
  SearchIcon,
  NotLoaded
} from "../../style/components/general.js";
import {flex, z} from "../../style/components/mixins.js";

const PAGE_LIMIT = 5;
/** Leftover scroll still treated as “needs more rows” so a tiny bar isn’t the only way to load. */
const VIEWPORT_FILL_SLACK_PX = 100;
const CHECKSHEETS_API = `${getApiBase()}/api/v1/checksheets`;

const fetchChecksheetsByIds = (ids, signal) =>
  Promise.all(
    ids.map(id =>
      axios.get(`${CHECKSHEETS_API}/${id}`, {
        params: {withState: true, withCurrentState: true, withCounts: true},
        withCredentials: true,
        signal
      })
    )
  );

const dedupeChecksheetsById = list => {
  const seen = new Set();
  return (list ?? []).filter(({id}) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/** True when content fits the viewport or only a small, easy-to-miss scroll remains. */
const contentFillsViewport = (
  {scrollHeight, clientHeight} = {},
  slackPx = VIEWPORT_FILL_SLACK_PX
) => scrollHeight <= clientHeight + slackPx;

const fillsViewport = () => {
  const {scrollHeight, clientHeight} = document.documentElement;
  return contentFillsViewport({scrollHeight, clientHeight});
};

const Checksheets = ({facilityId, locks, actionMenuEl}) => {
  const isMounted = useMountedState();
  const navigate = useNavigate();
  const {slug} = useParams();

  const {getNotifications} = useContext(NotificationContext);
  const {roleCanAccessResource, currentUser, atLeast} = useContext(AuthContext);
  const {facility} = useContext(FacilityNavContext);
  const {settings} = useContext(SettingsContext);

  const {api} = useApi("checksheets");
  const {api: apiChecksheetRecords} = useApi("checksheet-records");

  // Filtered Overdue / Outstanding groups the API says the just-published draft won't reach.
  const [filteredNotifications, setFilteredNotifications] = useState(null);

  const initialized = useRef(false);
  /** False when the last page fetch added no rows after restrictTo filtering. */
  const lastBatchAddedVisibleRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [checksheets, setChecksheets] = useState(undefined);
  const [currentChecksheet, setCurrentChecksheet] = useState(undefined);
  const [queryInfo, setQueryInfo] = useState(undefined);
  /** Pending record deep link until target checksheet is in the loaded list. */
  const [recordDeepLink, setRecordDeepLink] = useState(null);
  /** False until URL deep link (incl. record fetch) is resolved — gates TableRecord mount. */
  const [tablesReady, setTablesReady] = useState(false);
  /** Record modal at page level while the checksheet list loads (avoids waiting on TableRecord). */
  const [recordInfoModal, setRecordInfoModal] = useState(null);
  const [readyToScroll, setReadyToScroll] = useState(undefined);
  const [triggerToggleAll, setTriggerToggleAll] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [tableQuery, setTableQuery] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  // Modals
  const [showModalExport, setShowModalExport] = useState(false);
  const [showModalArchive, setShowModalArchive] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalViewAll, setShowModalViewAll] = useState(false);

  const actionsMenuRef = useRef(null);
  const currentChecksheetUpdater = useRef(undefined);
  const tablesOpen = useRef({});
  const fetchingChecksheetForDeepLink = useRef(false);
  /** Bumped once per frequency batch so open tables refetch records in sync with header updates. */
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  const frequencyRefreshControllerRef = useRef(null);

  const socket = useSocket();

  const filterVisibleChecksheets = useCallback(
    batch =>
      (batch ?? []).filter(
        ({restrictTo}) =>
          !restrictTo ||
          atLeast("admin") ||
          restrictTo?.map(({id: roleId}) => roleId)?.includes(currentUser?.role.id)
      ),
    [atLeast, currentUser?.role.id]
  );

  const checksheetListParams = useCallback(
    page => ({
      facilityId,
      includeDrafts:
        roleCanAccessResource("facility_checksheet", "update") || atLeast("admin"),
      orderBy: "asc",
      groupBy: "frequency",
      withState: true,
      withCurrentState: true,
      withCounts: true,
      withAllStates: true,
      page: page || 1,
      limit: PAGE_LIMIT
    }),
    [atLeast, facilityId, roleCanAccessResource]
  );

  const archivedChecksheets = checksheets?.filter(
    ({isArchived, name}) =>
      isArchived && (!tableQuery || name.toLowerCase().includes(tableQuery.toLowerCase()))
  );
  const activeChecksheets = checksheets?.filter(
    ({isArchived, name}) =>
      !isArchived && (!tableQuery || name.toLowerCase().includes(tableQuery.toLowerCase()))
  );

  const handleFrequencyChange = useCallback(
    async ({checksheetIds, checksheetId}) => {
      const ids =
        checksheetIds?.map(id => `${id}`) ?? (checksheetId != null ? [`${checksheetId}`] : []);
      if (!ids.length) return;

      frequencyRefreshControllerRef.current?.abort();
      const controller = new AbortController();
      frequencyRefreshControllerRef.current = controller;

      let responses;
      try {
        responses = await fetchChecksheetsByIds(ids, controller.signal);
      } catch (e) {
        if (controller.signal.aborted || e?.code === "ERR_CANCELED") return;
        throw e;
      }

      if (controller.signal.aborted) return;

      const updated = {};
      responses.forEach(({status, data}) => {
        const visible = filterVisibleChecksheets([data]);
        if (status === 200 && visible.length) {
          const [checksheet] = visible;
          updated[checksheet.id] = checksheet;
        }
      });

      if (!Object.keys(updated).length) return;

      flushSync(() => {
        setChecksheets(prev =>
          dedupeChecksheetsById((prev ?? []).map(cs => (updated[cs.id] ? updated[cs.id] : cs)))
        );
        setRecordsRefreshKey(k => k + 1);
      });
    },
    [filterVisibleChecksheets]
  );

  useEffect(() => {
    socket.on(LISTEN_NOTIFY_FREQUENCY_CHANGE, handleFrequencyChange);

    return () => {
      socket.off(LISTEN_NOTIFY_FREQUENCY_CHANGE, handleFrequencyChange);
    };
  }, [socket, handleFrequencyChange]);

  const resolveRecordDeepLink = useCallback(
    async (recordId, checksheetIdFromUrl, searchQueryFromUrl) => {
      const {status, data: record} = await apiChecksheetRecords.callGet(recordId);
      if (status !== 200 || record?.facilityChecksheetId == null) return null;

      const checksheetId = checksheetIdFromUrl ?? record.facilityChecksheetId;
      const query =
        searchQueryFromUrl ??
        formatRecordDateSearchQuery(record, settings?.timezone || "US/Pacific");

      const {status: csStatus, data: checksheet} = await api.callGet(checksheetId, {
        withState: true,
        withCurrentState: true,
        withCounts: true
      });

      return {
        checksheetId,
        recordId,
        query,
        record,
        isArchived: csStatus === 200 && !!checksheet?.isArchived,
        checksheet: csStatus === 200 ? checksheet : null
      };
    },
    [api, apiChecksheetRecords, settings?.timezone]
  );

  const applyScrollDeepLink = useCallback(parsed => {
    const {hash, search} = window.location;
    const searchRaw = (search || "").replace(/^\?/, "");

    if (parsed.checksheetId == null) return;

    if (!searchRaw && hash && parsed.recordId == null) {
      return;
    }

    setQueryInfo({
      checksheetId: parsed.checksheetId,
      recordId: undefined,
      query: parsed.searchQuery
    });
  }, []);

  const scrollToTable = useCallback(() => {
    const tableId = queryInfo?.checksheetId;
    if (tableId == null) return;

    const table = document.getElementById(`c${tableId}`);
    if (table) {
      const y = table.getBoundingClientRect().top - 100;
      window.scrollBy({behavior: "smooth", top: y});
    }
  }, [queryInfo?.checksheetId]);

  const getChecksheets = useCallback(
    (page, refresh) => {
      setLoading(true);

      if (refresh) {
        setCurrentPage(1);
        setPages(1);
        setChecksheets(undefined);
        lastBatchAddedVisibleRef.current = true;
      }

      return api
        .callGet(null, checksheetListParams(page))
        .then(response => {
          if (response?.status === 200) {
            const {data} = response;
            const batch = filterVisibleChecksheets(data.checksheets ?? data);
            setCurrentPage(data.page ?? page ?? 1);
            setPages(data.pages ?? 1);
            lastBatchAddedVisibleRef.current = batch.length > 0;
            setChecksheets(prev =>
              dedupeChecksheetsById(refresh || !prev ? batch : [...(prev ?? []), ...batch])
            );
          }
          return response;
        })
        .finally(() => {
          setLoading(false);
          setOverflow(false);
        });
    },
    [api, filterVisibleChecksheets, checksheetListParams]
  );

  const mergeChecksheet = useCallback(
    checksheet => {
      const visible = filterVisibleChecksheets([checksheet]);
      if (!visible.length) return;

      setChecksheets(prev => dedupeChecksheetsById(prev ? [...prev, ...visible] : visible));
    },
    [filterVisibleChecksheets]
  );

  const activateRecordDeepLink = useCallback((target, {skipRecordModal = false} = {}) => {
    if (target.isArchived) setArchivedOpen(true);
    setQueryInfo({
      checksheetId: target.checksheetId,
      recordId: skipRecordModal ? undefined : target.recordId,
      query: target.query,
      skipRecordModal
    });
    setRecordDeepLink(null);
    setReadyToScroll(true);
  }, []);

  const closeRecordInfoModal = useCallback(
    visible => {
      if (visible) return;
      const modal = recordInfoModal;
      setRecordInfoModal(null);
      if (modal?.checksheetId != null) {
        navigate(buildChecksheetDateScrollUrl(slug, modal.query, modal.checksheetId), {
          replace: true
        });
      }
    },
    [recordInfoModal, navigate, slug]
  );

  useEffect(() => {
    if (!recordDeepLink || checksheets === undefined) return;

    const {checksheetId, checksheet: prefetched} = recordDeepLink;
    if (checksheets.some(({id}) => id === checksheetId)) {
      activateRecordDeepLink(recordDeepLink, {skipRecordModal: Boolean(recordInfoModal)});
      return;
    }

    if (prefetched) {
      if (filterVisibleChecksheets([prefetched]).length) mergeChecksheet(prefetched);
      else setRecordDeepLink(null);
      return;
    }

    if (loading) return;

    if (currentPage < pages) {
      getChecksheets(currentPage + 1);
      return;
    }

    if (fetchingChecksheetForDeepLink.current) return;
    fetchingChecksheetForDeepLink.current = true;

    api
      .callGet(checksheetId, {withState: true, withCurrentState: true, withCounts: true})
      .then(({status, data}) => {
        if (status === 200 && data) mergeChecksheet(data);
        else setRecordDeepLink(null);
      })
      .finally(() => {
        fetchingChecksheetForDeepLink.current = false;
      });
  }, [
    recordDeepLink,
    checksheets,
    loading,
    currentPage,
    pages,
    getChecksheets,
    mergeChecksheet,
    filterVisibleChecksheets,
    api,
    activateRecordDeepLink,
    recordInfoModal
  ]);

  useEffect(() => {
    initialized.current = false;
    setRecordDeepLink(null);
    setQueryInfo(undefined);
    setRecordInfoModal(null);
    setTablesReady(false);
    fetchingChecksheetForDeepLink.current = false;
  }, [facilityId]);

  const handleScroll = useCallback(() => {
    const {scrollHeight, clientHeight, scrollTop} = document.documentElement;
    const maxScrollTop = scrollHeight - clientHeight;

    if (scrollTop + 1 >= maxScrollTop && currentPage < pages && !loading) setOverflow(true);
  }, [currentPage, loading, pages]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const visibleCount = (activeChecksheets?.length ?? 0) + (archivedChecksheets?.length ?? 0);

  // Load more when scrolled to bottom, or when content is shorter than the viewport.
  // Do not auto-fill the viewport when nothing is visible yet (e.g. restrictTo filtered
  // every item on the page) — that would fetch every page with no UI benefit.
  useEffect(() => {
    const shouldFillViewport =
      fillsViewport() && visibleCount > 0 && lastBatchAddedVisibleRef.current;

    if (!loading && checksheets && currentPage < pages && (overflow || shouldFillViewport)) {
      getChecksheets(currentPage + 1);
    }
  }, [
    activeChecksheets?.length,
    archivedChecksheets?.length,
    checksheets,
    overflow,
    currentPage,
    pages,
    loading,
    getChecksheets,
    visibleCount
  ]);

  useEffect(() => {
    if (!isMounted() || initialized.current) return;
    initialized.current = true;

    const bootstrap = async () => {
      const {hash, search} = window.location;
      const parsed = parseChecksheetsDeepLink({search, hash});

      if (parsed.recordId != null) {
        const resolved = await resolveRecordDeepLink(
          parsed.recordId,
          parsed.checksheetId,
          parsed.searchQuery
        );

        if (resolved?.record && resolved.checksheet) {
          if (resolved.isArchived) setArchivedOpen(true);
          setRecordInfoModal({
            record: resolved.record,
            checksheet: resolved.checksheet,
            checksheetId: resolved.checksheetId,
            query: resolved.query
          });
          setRecordDeepLink(resolved);
        } else if (resolved) {
          setRecordDeepLink(resolved);
        }
      } else {
        applyScrollDeepLink(parsed);
      }

      setTablesReady(true);
      getChecksheets(undefined, true);
    };

    bootstrap();
  }, [
    isMounted,
    getChecksheets,
    resolveRecordDeepLink,
    applyScrollDeepLink,
    filterVisibleChecksheets,
    activateRecordDeepLink
  ]);

  const publishDraft = id =>
    api.callPut(id, {draft: false}).then(response => {
      getChecksheets(null, true);
      if (response?.status === 200)
        setFilteredNotifications(response.data?.data?.filteredNotifications);
    });

  const openExport = checksheet => {
    if (checksheet?.builder) {
      setCurrentChecksheet(checksheet);
      setShowModalExport(true);
      return;
    }

    // Slim list omits builder; hydrate before opening export field picker.
    api
      .callGet(checksheet.id, {withState: true, withCurrentState: true, withCounts: true})
      .then(({status, data}) => {
        if (status === 200 && data) {
          setCurrentChecksheet(data);
          setShowModalExport(true);
        }
      });
  };

  const openArchive = checksheet => {
    setCurrentChecksheet(checksheet);
    setShowModalArchive(true);
  };

  const openDelete = checksheet => {
    setCurrentChecksheet(checksheet);
    setShowModalDelete(true);
  };

  const archiveChecksheet = () =>
    api.callPut(currentChecksheet.id, {archive: true}).then(() => {
      getChecksheets(null, true);
      setShowModalArchive(false);
      getNotifications(slug);
    });

  const deleteChecksheet = () =>
    api.callDelete(currentChecksheet.id).then(() => {
      getChecksheets(null, true);
      setShowModalDelete(false);
      getNotifications(slug);
    });

  const restoreChecksheet = checksheet =>
    api.callPut(checksheet.id, {archive: false}).then(response => {
      getChecksheets(null, true);
      getNotifications(slug);
      if (response?.status === 200)
        setFilteredNotifications(response.data?.data?.filteredNotifications);
    });

  const handleViewAll = (checksheet, update) => {
    currentChecksheetUpdater.current = update;
    setCurrentChecksheet(checksheet);
    setShowModalViewAll(true);
  };

  const onOpen = useCallback(
    (state, checksheetId) => {
      tablesOpen.current[checksheetId] = state;
      if (
        activeChecksheets?.every(({id}) => tablesOpen.current[id]) &&
        (!archivedOpen || archivedChecksheets?.every(({id}) => tablesOpen.current[id]))
      ) {
        setAllOpen(true);
      } else setAllOpen(false);
      setTriggerToggleAll(false);
    },
    [activeChecksheets, archivedChecksheets, archivedOpen]
  );

  useEffect(() => {
    if (
      activeChecksheets?.every(({id}) => tablesOpen.current[id]) &&
      (!archivedOpen || archivedChecksheets?.every(({id}) => tablesOpen.current[id]))
    ) {
      setAllOpen(true);
    } else setAllOpen(false);
    setTriggerToggleAll(false);
  }, [activeChecksheets, archivedChecksheets, archivedOpen, tableQuery]);

  useEffect(() => {
    if (readyToScroll) {
      scrollToTable();
      setReadyToScroll(false); // Reset so it doesn’t run again unnecessarily
    }
  }, [readyToScroll, scrollToTable]);

  const handleSearch = e => {
    setTableQuery(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (!showActionsMenu) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    const handleEscape = e => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showActionsMenu]);

  const noChecksheets =
    (!activeChecksheets || activeChecksheets.length === 0) &&
    (!archivedChecksheets || archivedChecksheets.length === 0);

  const handledOpen = useRef(false);

  const actionsMenu = (
    <ActionsMenuWrapper ref={actionsMenuRef}>
      <ActionMenuButton
        title="More"
        type="button"
        $active={!!tableQuery || showActionsMenu}
        aria-label="Checksheet actions"
        aria-expanded={showActionsMenu}
        aria-haspopup="true"
        data-testid="checksheets.moreActions"
        onClick={() => setShowActionsMenu(prev => !prev)}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </ActionMenuButton>
      {showActionsMenu && (
        <ActionsMenu>
          <MenuSearch>
            <SearchWrapper>
              <SearchIcon>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
              <StyledSearch
                type="text"
                name="search"
                placeholder="Checksheet Table Name..."
                autoFocus
                value={tableQuery || ""}
                onChange={handleSearch}
              />
            </SearchWrapper>
          </MenuSearch>
          {!noChecksheets && (
            <ActionItem
              type="button"
              title={allOpen ? "Collapse All Tables" : "Expand All Tables"}
              onClick={() => {
                setTriggerToggleAll(true);
                setShowActionsMenu(false);
              }}>
              {allOpen ? "Collapse All Tables" : "Expand All Tables"}
            </ActionItem>
          )}
        </ActionsMenu>
      )}
    </ActionsMenuWrapper>
  );

  return (
    <>
      <OutermostWrapper>
        {actionMenuEl
          ? createPortal(actionsMenu, actionMenuEl)
          : checksheets &&
            (!noChecksheets || tableQuery) && <InlineMenu>{actionsMenu}</InlineMenu>}

        {checksheets && noChecksheets && tablesReady && (
          <NotFound>
            <Text>No checksheets {tableQuery ? "match your search" : "found"}.</Text>
          </NotFound>
        )}
        {tablesReady &&
          activeChecksheets?.length > 0 &&
          activeChecksheets.map((checksheet, index) => (
            <div
              id={`c${checksheet.id}`}
              key={checksheet.id}
              ref={el => {
                if (
                  el &&
                  (window.location.hash === `#${checksheet.id}` ||
                    queryInfo?.checksheetId === checksheet.id)
                ) {
                  if (readyToScroll === undefined) setReadyToScroll(true);
                }
              }}>
              <TableRecord
                index={index + 1}
                checksheet={checksheet}
                recordsRefreshKey={recordsRefreshKey}
                publishDraft={publishDraft}
                currentChecksheet={setCurrentChecksheet}
                showExport={openExport}
                archiveChecksheet={openArchive}
                showDelete={openDelete}
                restoreChecksheet={restoreChecksheet}
                locks={locks}
                handleViewAll={handleViewAll}
                queryInfo={queryInfo?.checksheetId === checksheet.id ? queryInfo : undefined}
                setQueryInfo={setQueryInfo}
                refresh={() => getChecksheets(null, true)}
                onOpen={onOpen}
                triggerOpen={triggerToggleAll ? !allOpen : undefined}
                handledOpen={handledOpen}
              />
            </div>
          ))}
        {tablesReady && archivedChecksheets?.length > 0 && (
          <Accordion
            label="Archived Checksheets"
            toggleOpen={archivedOpen}
            handleOpen={() => setArchivedOpen(prev => !prev)}>
            <Wrapper>
              {archivedChecksheets.map((checksheet, index) => (
                <div
                  id={`c${checksheet.id}`}
                  key={checksheet.id}
                  ref={el => {
                    if (
                      el &&
                      (window.location.hash === `#${checksheet.id}` ||
                        queryInfo?.checksheetId === checksheet.id)
                    ) {
                      if (readyToScroll === undefined) setReadyToScroll(true);
                    }
                  }}>
                  <TableRecord
                    index={(activeChecksheets?.length || 0) + index + 1}
                    checksheet={checksheet}
                    recordsRefreshKey={recordsRefreshKey}
                    publishDraft={publishDraft}
                    currentChecksheet={setCurrentChecksheet}
                    showExport={openExport}
                    archiveChecksheet={openArchive}
                    showDelete={openDelete}
                    restoreChecksheet={restoreChecksheet}
                    locks={locks}
                    handleViewAll={handleViewAll}
                    queryInfo={queryInfo?.checksheetId === checksheet.id ? queryInfo : undefined}
                    setQueryInfo={setQueryInfo}
                    refresh={() => getChecksheets(null, true)}
                    onOpen={onOpen}
                    triggerOpen={triggerToggleAll && archivedOpen ? !allOpen : undefined}
                    handledOpen={handledOpen}
                  />
                </div>
              ))}
            </Wrapper>
          </Accordion>
        )}
        {(loading || !tablesReady) && !recordInfoModal && (
          <NotLoaded>
            <Loader data-testid="checksheets.loader" />
          </NotLoaded>
        )}
      </OutermostWrapper>

      {showModalArchive && (
        <ModalArchive
          visible={showModalArchive}
          setVisible={setShowModalArchive}
          archive={archiveChecksheet}
          loading={loading}
        />
      )}

      {showModalDelete && (
        <ModalDelete
          visible={showModalDelete}
          setVisible={setShowModalDelete}
          confirmDelete={deleteChecksheet}
          title="Delete Checksheet"
          loading={loading}
        />
      )}

      {showModalExport && (
        <ModalExport
          visible={showModalExport}
          setVisible={setShowModalExport}
          checksheet={currentChecksheet}
          hasAddress={!!facility?.primaryAddress}
        />
      )}

      {recordInfoModal && (
        <ModalRecordView
          visible
          setVisible={closeRecordInfoModal}
          record={recordInfoModal.record}
          checksheet={recordInfoModal.checksheet}
          locks={locks}
          updateData={() => getChecksheets(null, true)}
        />
      )}

      {showModalViewAll && (
        <ModalViewAllRecords
          visible={showModalViewAll}
          setVisible={state => {
            if (!state) {
              currentChecksheetUpdater.current = undefined;
              setCurrentChecksheet(undefined);
            }
            setShowModalViewAll(state);
          }}
          target={currentChecksheet}
          reload={currentChecksheetUpdater.current}
          locks={locks}
        />
      )}

      <ModalFilteredNotifications
        notifications={filteredNotifications}
        onClose={() => setFilteredNotifications(null)}
        slug={slug}
      />
    </>
  );
};

Checksheets.propTypes = {
  facilityId: PropTypes.number.isRequired,
  locks: PropTypes.objectOf(PropTypes.any),
  actionMenuEl: PropTypes.object
};

// Style Overrides

const OutermostWrapper = styled.div`
  ${flex("column", "nowrap")}

  margin-bottom: ${pad * 2}px;
`;

const Wrapper = styled.div`
  ${flex("column", "nowrap")}

  margin-bottom: ${pad * 2}px;
  gap: ${pad * 0.7}px;
`;

const NotFound = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 85%;
  min-height: 75px;
  padding: ${pad}px;
  margin-right: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${props => props.theme.secondary};
  width: 100%;
`;

const InlineMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${pad}px;
`;

const ActionsMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const ActionMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 36px;
  min-height: 32px;
  padding-left: ${pad}px;
  color: ${({theme}) => theme.secondary};
  background-color: transparent;
  transition: ${transition};

  svg {
    fill: ${({theme}) => theme.secondary};
  }

  &:hover {
    background-color: ${({theme}) => theme.secondary};
    color: ${({theme}) => theme.tertiary};

    svg {
      fill: ${({theme}) => theme.tertiary};
    }
  }

  ${({$active, theme}) =>
    $active &&
    `
      background-color: ${theme.secondary};
      color: ${theme.tertiary};

      svg {
        fill: ${theme.tertiary};
      }
    `}
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + ${pad / 2}px);
  right: 0;
  min-width: 220px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("higher")};
  overflow: hidden;
`;

const MenuSearch = styled.div`
  padding: ${pad / 2}px;

  ${SearchWrapper} {
    width: 100%;
  }

  ${SearchIcon} {
    top: 50%;
    transform: translateY(-50%);
  }
`;

const ActionItem = styled.button`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 2}px;
  width: 100%;
  padding: ${pad / 2}px ${pad}px;
  background: ${({theme}) => theme.tertiary};
  color: ${({theme}) => theme.secondary};
  text-align: left;

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
  }
`;

const StyledSearch = styled(Search)`
  padding-left: 30px;

  &[type="text"] {
    max-width: 100%;
    width: 100%;
  }
`;

export {contentFillsViewport, VIEWPORT_FILL_SLACK_PX, PAGE_LIMIT};
export default Checksheets;
